import {
    type NodeCfg,
    type NodeSocketKindMap,
} from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { getUID } from '@redviking/argonaut-util/src/getUID';
import {
    type DeletableTextWithTypeSelectorCfg,
    UiFlowNodeControl,
    type UiFlowNodeControlConfig,
} from '../../controls/UiFlowNodeControl';
import type { RequiredSockets, UiNodeSpec } from './UiNodeSpec';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import { accessor } from '@redviking/argonaut-core-ui/src/store';
import { removeNodeSocket } from '../../sync-utils/remove-node-socket';

const getAllFunctionalOutputsNodes = (): NodeCfg<'functionalOutputs'>[] => {
    const activeFlowId = accessor.appletDesign.activeFlowId;
    if (!activeFlowId) {
        return [];
    }
    const entityData = accessor.entityAsType('appletDesignVersion');
    const flow = entityData?.config.flows[activeFlowId];
    if (!flow) {
        return [];
    }
    return Object.values(flow.nodes).filter((n): n is NodeCfg<'functionalOutputs'> => n.kind === 'functionalOutputs');
};

const applyChangeToAllFunctionalOutputs = (newControlValues: NodeCfg<'functionalOutputs'>['controlValues']) => {
    for (const existingFunctionalOutputNode of getAllFunctionalOutputsNodes()) {
        const newNodeCfg: NodeCfg<'functionalOutputs'> = {
            ...existingFunctionalOutputNode,
            controlValues: newControlValues,
        };
        createNodeSocket({
            newNodeCfg,
        });
    }
};

export const functionalOutputsUiNodeSpec: UiNodeSpec<'functionalOutputs'> = {
    outputs: () => ({}),
    nodeCategory: 'other',
    kindLabel: 'Functional Outputs',
    bypassSocketDefaultDisable: true,
    validator: node => {
        const errors: Record<string, string> = {};
        node.controlValues.outputs.forEach((output, outputIdx) => {
            if (!output.name) {
                errors.outputs = 'One or more output names are empty';
            } else {
                const otherOutputs = [
                    ...node.controlValues.outputs.slice(0, outputIdx),
                    ...node.controlValues.outputs.slice(outputIdx + 1),
                ];
                if (otherOutputs.find(o => o.name === output.name)) {
                    errors.outputs = 'One or more outputs share a name';
                }
            }
        });
        return Object.keys(errors).length ? errors : true;
    },
    requiredSockets: node => {
        const ret: ReturnType<RequiredSockets<typeof node.kind, NodeSocketKindMap[typeof node.kind]>> = {
            required: [],
            // this conditionallyRequired statement basically says at least one input socket must be wired up
            conditionallyRequired: [
                {
                    kind: 'or',
                    availableOptions: [ 'finish', ...node.controlValues.outputs.map((_, idx) => `input${idx}`) as `input${number}`[] ],
                },
            ],
        };
        return ret;
    },
    suppressNode: () => {
        const activeFlowId = accessor.appletDesign.activeFlowId;
        const entityData = accessor.entityAsType('appletDesignVersion');
        const flows = entityData?.config.flows;
        if (!activeFlowId || !flows) {
            return true;
        }
        const flow = flows[activeFlowId];
        if (!flow) {
            return true;
        }
        return flow.kind === 'regular';
    },
    cfg: () => {
        const ret: NodeCfg<'functionalOutputs'> = {
            id: getUID(),
            disabled: false,
            kind: 'functionalOutputs',
            controlValues: {
                outputs: [],
            },
        };
        const activeFlowId = accessor.appletDesign.activeFlowId;
        if (!activeFlowId) {
            return ret;
        }
        const entityData = accessor.entityAsType('appletDesignVersion');
        const flow = entityData?.config.flows[activeFlowId];
        if (!flow) {
            return ret;
        }

        // we can grab any existing functional outputs node as our template since they all should have the same outputs
        const existingFunctionalOutputs = Object.values(flow.nodes).find((n): n is NodeCfg<'functionalOutputs'> => n.kind === 'functionalOutputs');
        if (!existingFunctionalOutputs) {
            return ret;
        }

        ret.controlValues.outputs = existingFunctionalOutputs.controlValues.outputs;
        return ret;
    },
    controls: getNodeCfg => ({
        outputs: new UiFlowNodeControl({
            cfg: {
                kind: 'button',
                label: 'Add Output',
                onClick: () => {
                    applyChangeToAllFunctionalOutputs({
                        ...getNodeCfg().controlValues,
                        outputs: [
                            ...getNodeCfg().controlValues.outputs,
                            {
                                name: '',
                                dataType: 'string',
                            },
                        ],
                    });
                },
            },
            index: 1,
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.outputs,
        }),
    }),
    inputs: cfg => {
        const inputs: ReturnType<UiNodeSpec<'functionalOutputs'>['inputs']> = {
            finish: { socket: 'event', label: 'Finish' },
        };
        for (let i = 0; i < cfg.controlValues.outputs.length; i++) {
            const input = cfg.controlValues.outputs[i];
            const controlCfg: DeletableTextWithTypeSelectorCfg<NodeCfg<'functionalOutputs'>['controlValues']['outputs'][number]['dataType']> = {
                valuePlaceholder: 'Output Name',
                selectionPlaceholder: 'Output Type',
                kind: 'deletableTextControlWithTypeSelector',
                allowedDataTypes: [ 'string', 'number', 'boolean' ],
                getValue: nodeCfg => {
                    if (nodeCfg.kind !== 'functionalOutputs') {
                        return null;
                    }
                    const outputFromCfg = nodeCfg.controlValues.outputs[i];
                    return {
                        newValue: outputFromCfg.name,
                        dataType: outputFromCfg.dataType,
                    };
                },
                onChange: newValue => {
                    const newOutputs: NodeCfg<'functionalOutputs'>['controlValues']['outputs'] = [
                        ...cfg.controlValues.outputs.slice(0, i),
                        {
                            name: newValue.newValue,
                            dataType: newValue.dataType,
                        },
                        ...cfg.controlValues.outputs.slice(i + 1),
                    ];

                    applyChangeToAllFunctionalOutputs({
                        ...cfg.controlValues,
                        outputs: newOutputs,
                    });
                },
                async onDelete () {
                    for (const existingFunctionalOutputNode of getAllFunctionalOutputsNodes()) {
                        await removeNodeSocket<'functionalOutputs'>({
                            direction: 'output',
                            removalMethod: {
                                index: i,
                                prefix: 'input',
                                kind: 'indexedIdentifier',
                            },
                            newNodeCfg: {
                                ...existingFunctionalOutputNode,
                                controlValues: {
                                    ...existingFunctionalOutputNode.controlValues,
                                    outputs: [
                                        ...existingFunctionalOutputNode.controlValues.outputs.slice(0, i),
                                        ...existingFunctionalOutputNode.controlValues.outputs.slice(i + 1),
                                    ],
                                },
                            },
                        });
                    }
                },
            };
            const control: UiFlowNodeControl.Opts<unknown> = ({
                nodeCfg: cfg,
                value: cfg.controlValues.outputs[i],
                cfg: controlCfg as (UiFlowNodeControlConfig & { kind: 'deletableTextControlWithTypeSelector' }),
            });
            inputs[`input${i}`] = ({
                socket: (input.dataType),
                control,
            });
        }
        return inputs;
    },
};
